import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  CalendarDays,
  Clock,
  Receipt,
  AlertTriangle,
  UserPlus,
  Bookmark,
  Wrench,
} from "lucide-react";

import { pageTransition, formatCurrency } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import { getOverview, type OverviewReport } from "@/api/reports";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { Tabs } from "@/components/ui/Tabs";
import { StatCard } from "@/components/shared/StatCard";
import { AnalyticsReports } from "./AnalyticsReports";

const SEVERITY_COLORS: Record<string, string> = {
  LOW: "bg-text-muted/40",
  MEDIUM: "bg-accent",
  HIGH: "bg-warning",
  URGENT: "bg-danger",
};

const SEVERITY_LABELS: Record<string, string> = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  URGENT: "Urgent",
};

export function ReportsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState<OverviewReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    (async () => {
      try {
        const result = await getOverview();
        setData(result);
      } catch {
        addToast("error", "Failed to load reports");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [addToast]);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "analytics", label: "Analytics" },
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Reports"
        subtitle="Organization-wide analytics and insights"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate("/reports/saved")}
            >
              <Bookmark className="h-4 w-4" />
              Saved reports
            </Button>
            <Button onClick={() => navigate("/reports/builder")}>
              <Wrench className="h-4 w-4" />
              Build custom report
            </Button>
          </div>
        }
      />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {activeTab === "analytics" && <AnalyticsReports />}

      {activeTab === "overview" && isLoading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-56" />
            ))}
          </div>
        </>
      )}

      {activeTab === "overview" && data && (() => {
        const { headcount, leave, time, invoices, complaints } = data;
        const maxDept = Math.max(1, ...headcount.by_department.map((d) => d.count));
        const maxLeaveTotal = Math.max(1, ...leave.by_type.map((l) => l.total));
        const maxSeverity = Math.max(
          1,
          ...Object.values(complaints.by_severity).map((v) => v || 0),
        );
        return (
      <>
      {/* Top-level KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Active Headcount"
          value={headcount.total_active}
          icon={Users}
        />
        <StatCard
          label="New Hires (90d)"
          value={headcount.new_hires_90d}
          icon={UserPlus}
        />
        <StatCard
          label="Hours Logged"
          value={time.total_hours.toFixed(0)}
          icon={Clock}
        />
        <StatCard
          label="Invoiced"
          value={formatCurrency(invoices.grand_total)}
          icon={Receipt}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Headcount by department */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-accent" />
            <h3 className="font-semibold font-heading text-text-primary">
              Headcount by department
            </h3>
          </div>
          <div className="space-y-3">
            {headcount.by_department.length === 0 ? (
              <p className="text-sm text-text-muted">No department data</p>
            ) : (
              headcount.by_department.map((d) => (
                <div key={d.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-text-primary">{d.name}</span>
                    <span className="text-text-muted">{d.count}</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all"
                      style={{ width: `${(d.count / maxDept) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Leave utilization */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="h-4 w-4 text-accent" />
            <h3 className="font-semibold font-heading text-text-primary">
              Leave utilization
            </h3>
          </div>
          <div className="space-y-4">
            {leave.by_type.length === 0 ? (
              <p className="text-sm text-text-muted">No leave data</p>
            ) : (
              leave.by_type.map((l) => (
                <div key={l.type}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-text-primary font-medium">{l.type}</span>
                    <span className="text-xs text-text-muted">
                      {l.used.toFixed(0)} used / {l.total.toFixed(0)} total (
                      {l.utilization_pct}%)
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${(l.used / maxLeaveTotal) * 100}%` }}
                    />
                    <div
                      className="h-full bg-warning"
                      style={{ width: `${(l.pending / maxLeaveTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
            <div className="flex items-center gap-4 text-xs text-text-muted pt-1">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-accent" />
                Used
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-warning" />
                Pending
              </span>
            </div>
          </div>
        </Card>

        {/* Time tracking breakdown */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-accent" />
            <h3 className="font-semibold font-heading text-text-primary">
              Time tracking
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-text-muted">Regular</p>
              <p className="text-xl font-semibold text-text-primary">
                {time.regular_hours.toFixed(0)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Overtime</p>
              <p className="text-xl font-semibold text-warning">
                {time.overtime_hours.toFixed(0)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Total</p>
              <p className="text-xl font-semibold text-accent">
                {time.total_hours.toFixed(0)}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Timesheets by status
            </p>
            <div className="space-y-1.5">
              {Object.entries(time.status_counts).map(([s, c]) => (
                <div key={s} className="flex items-center justify-between text-sm">
                  <span className="text-text-primary">{s}</span>
                  <span className="text-text-muted">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Invoice status */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-4 w-4 text-accent" />
            <h3 className="font-semibold font-heading text-text-primary">
              Invoices
            </h3>
          </div>
          <div className="space-y-2">
            {Object.entries(invoices.by_status_count).map(([s, cnt]) => {
              const total = invoices.by_status_total[s] || 0;
              return (
                <div
                  key={s}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-background/50"
                >
                  <div>
                    <p className="text-sm font-medium text-text-primary">{s}</p>
                    <p className="text-xs text-text-muted">{cnt} invoice{cnt === 1 ? "" : "s"}</p>
                  </div>
                  <p className="text-sm font-semibold text-text-primary">
                    {formatCurrency(total)}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Complaint severity breakdown */}
        <Card className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-accent" />
            <h3 className="font-semibold font-heading text-text-primary">
              Complaints
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-3">
                By severity
              </p>
              <div className="space-y-3">
                {Object.entries(complaints.by_severity).map(([sev, cnt]) => (
                  <div key={sev}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-text-primary">
                        {SEVERITY_LABELS[sev] || sev}
                      </span>
                      <span className="text-text-muted">{cnt}</span>
                    </div>
                    <div className="h-2 bg-background rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          SEVERITY_COLORS[sev] || "bg-accent"
                        }`}
                        style={{ width: `${((cnt || 0) / maxSeverity) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <div className="rounded-xl bg-background p-4">
                <p className="text-xs text-text-muted">Open</p>
                <p className="text-3xl font-semibold font-heading text-warning">
                  {complaints.open}
                </p>
              </div>
              <div className="rounded-xl bg-background p-4">
                <p className="text-xs text-text-muted">Closed</p>
                <p className="text-3xl font-semibold font-heading text-success">
                  {complaints.closed}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
      </>
        );
      })()}
    </motion.div>
  );
}
